import type { ConfigLayerDTO, ConfigScope, TargetDTO, VpsDTO } from '@devrobot/shared';

export function scopeQuery(scope: ConfigScope): { scope: string; ref?: string } {
  switch (scope.kind) {
    case 'environment':
      return { scope: 'environment' };
    case 'personal':
      return { scope: 'personal' };
    case 'vps':
      return { scope: 'vps', ref: scope.vpsId };
    case 'target':
      return { scope: 'target', ref: scope.targetId };
  }
}

export function scopeKey(scope: ConfigScope): string {
  const q = scopeQuery(scope);
  return q.ref ? `${q.scope}:${q.ref}` : q.scope;
}

/**
 * The scope a configuration layer was written at: `layer.scope` from the API. Older servers
 * only label VPS and target layers ("VPS eu-1", "target drb-shop-production"); for those the
 * label is matched against known VPSs and targets. Returns null when the layer cannot be
 * addressed (manifest default, unknown override).
 */
export function scopeForLayer(layer: Omit<ConfigLayerDTO, 'state' | 'version'> & Partial<ConfigLayerDTO>, vpsList: VpsDTO[] | undefined, targets: TargetDTO[] | undefined): ConfigScope | null {
  if (layer.scope) return layer.scope;
  switch (layer.layer) {
    case 'default':
      return null;
    case 'environment':
    case 'shared':
      return { kind: 'environment' };
    case 'personal':
      return { kind: 'personal' };
    case 'vps': {
      const name = layer.label.replace(/^VPS\s+/, '').replace(/\s+→ shared$/, '');
      const v = vpsList?.find((x) => x.name === name || x.id === name);
      return v ? { kind: 'vps', vpsId: v.id } : null;
    }
    case 'target': {
      const stack = layer.label.replace(/^target\s+/, '').replace(/\s+→ shared$/, '');
      const t = targets?.find((x) => x.stack === stack || x.id === stack);
      return t ? { kind: 'target', targetId: t.id } : null;
    }
  }
}

export function describeScope(scope: ConfigScope, env: string, vpsList?: VpsDTO[], targets?: TargetDTO[]): string {
  switch (scope.kind) {
    case 'environment':
      return `environment ${env}`;
    case 'personal':
      return 'your personal override';
    case 'vps':
      return `VPS override (${vpsList?.find((v) => v.id === scope.vpsId)?.name ?? scope.vpsId})`;
    case 'target':
      return `target override (${targets?.find((t) => t.id === scope.targetId)?.stack ?? scope.targetId})`;
  }
}
